import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Instagram, Youtube, Linkedin, ExternalLink, Trophy, Flame, Award } from "lucide-react";
import { saveReturnUrl } from "@/lib/postLoginRedirect";

const rememberCurrentPage = () => {
  if (typeof window === "undefined") return;
  const p = window.location.pathname + window.location.search;
  if (!p.startsWith("/login") && !p.startsWith("/register") && !p.startsWith("/auth/")) {
    saveReturnUrl(p);
  }
};

export default function Footer() {
  return (
    <footer className="mt-24 border-t border-white/10 bg-[#0a0a0a]">
      {/* ===== Highlighted Engagement Strip ===== */}
      <div className="relative bg-gradient-to-r from-[#FF0066]/10 via-fuchsia-500/5 to-amber-500/10 border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,0,102,0.12)_0%,_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-[11px] uppercase tracking-wider font-bold">
              🚀 Grow with us
            </div>
            <h3 className="mt-3 text-2xl md:text-3xl font-extrabold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Join the active CodeWithKamlesh community
            </h3>
            <p className="mt-2 text-sm text-white/60 max-w-xl mx-auto">Daily challenges, peer discussions, and real student success stories — everything to keep you motivated.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 max-w-4xl mx-auto">
            <Link to="/community" data-testid="footer-community" className="group relative rounded-xl bg-[#0a0a0a] border border-white/10 hover:border-[#FF0066]/40 p-4 transition overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-fuchsia-500/10 blur-2xl group-hover:bg-fuchsia-500/20 transition" />
              <div className="relative flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/20 flex items-center justify-center shrink-0"><MessageCircle className="w-4 h-4 text-fuchsia-300" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold">Community</div>
                  <div className="text-sm font-bold text-white mt-0.5">Discussion forum</div>
                  <div className="text-[11px] text-white/50 mt-1">Cross-batch doubts, wins, peer-to-peer learning</div>
                </div>
              </div>
            </Link>
            <Link to="/challenges" data-testid="footer-challenges" className="group relative rounded-xl bg-[#0a0a0a] border border-white/10 hover:border-amber-400/40 p-4 transition overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-amber-500/10 blur-2xl group-hover:bg-amber-500/20 transition" />
              <div className="relative flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-400/20 flex items-center justify-center shrink-0"><Flame className="w-4 h-4 text-amber-300" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-300 font-bold">Challenges</div>
                  <div className="text-sm font-bold text-white mt-0.5">Daily tasks · earn points</div>
                  <div className="text-[11px] text-white/50 mt-1">Mentor-set challenges + leaderboard prizes</div>
                </div>
              </div>
            </Link>
            <a href="/#results" data-testid="footer-results" className="group relative rounded-xl bg-[#0a0a0a] border border-white/10 hover:border-emerald-400/40 p-4 transition overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-emerald-500/10 blur-2xl group-hover:bg-emerald-500/20 transition" />
              <div className="relative flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-400/20 flex items-center justify-center shrink-0"><Trophy className="w-4 h-4 text-emerald-300" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold">Student Results</div>
                  <div className="text-sm font-bold text-white mt-0.5">Real placements & wins</div>
                  <div className="text-[11px] text-white/50 mt-1">2000+ students placed across India</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-12 gap-8">
        {/* Brand */}
        <div className="col-span-2 md:col-span-4">
          <div className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_batch-academy-2/artifacts/fpz48xo5_512X512%20CWK%20logo%20%281%29.png" alt="CodeWithKamlesh" className="w-10 h-10 rounded-lg object-contain" />
            <div className="font-display text-lg font-bold text-white">CodeWithKamlesh</div>
          </div>
          <p className="mt-4 text-sm text-white/60 max-w-xs leading-relaxed">
            Practical digital marketing training with live classes, real projects and career support — built for India's next generation of marketers.
          </p>
          <div className="mt-5 flex gap-3">
            <a href="https://www.instagram.com/codewithkamlesh" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#FF0066]/10 hover:border-[#FF0066]/40 transition" aria-label="Instagram"><Instagram className="w-4 h-4 text-white/80" /></a>
            <a href="https://www.youtube.com/@codewithkamlesh" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#FF0066]/10 hover:border-[#FF0066]/40 transition" aria-label="YouTube"><Youtube className="w-4 h-4 text-white/80" /></a>
            <a href="https://www.linkedin.com/in/kamleshsingad" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#FF0066]/10 hover:border-[#FF0066]/40 transition" aria-label="LinkedIn"><Linkedin className="w-4 h-4 text-white/80" /></a>
          </div>
        </div>

        {/* Quick links */}
        <div className="col-span-1 md:col-span-2">
          <div className="text-sm font-semibold text-white mb-4">Platform</div>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><Link to="/courses" className="hover:text-[#FF0066] transition">Courses</Link></li>
            <li><Link to="/webinars" className="hover:text-[#FF0066] transition">Live Classes</Link></li>
            <li><Link to="/login" onClick={rememberCurrentPage} className="hover:text-[#FF0066] transition">Student Login</Link></li>
            <li><Link to="/register" className="hover:text-[#FF0066] transition">Create Account</Link></li>
          </ul>
        </div>

        {/* Company */}
        <div className="col-span-1 md:col-span-2">
          <div className="text-sm font-semibold text-white mb-4">Company</div>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li><a href="/#about" className="hover:text-[#FF0066] transition">About</a></li>
            <li><a href="/#results" className="hover:text-[#FF0066] transition">Student Results</a></li>
            <li><a href="https://codewithkamlesh.in" target="_blank" rel="noreferrer" className="hover:text-[#FF0066] transition inline-flex items-center gap-1">Main Website <ExternalLink className="w-3 h-3" /></a></li>
            <li><a href="/refund" className="hover:text-[#FF0066] transition">Refund policy</a></li>
            <li><a href="/terms" className="hover:text-[#FF0066] transition">Terms</a></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="col-span-2 md:col-span-4">
          <div className="text-sm font-semibold text-white mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-white/60">
            <li className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-[#FF0066] mt-0.5 shrink-0" />
              <span>Apollo Square, 302, Dr. RS Bhandari Marg,<br/>Indore, MP 452001</span>
            </li>
            <li className="flex items-center gap-2.5">
              <Phone className="w-4 h-4 text-[#FF0066]" />
              <a href="tel:+919131341638" className="hover:text-[#FF0066] transition">+91 91313 41638</a>
            </li>
            <li className="flex items-center gap-2.5">
              <Mail className="w-4 h-4 text-[#FF0066]" />
              <a href="mailto:info@codewithkamlesh.in" className="hover:text-[#FF0066] transition">info@codewithkamlesh.in</a>
            </li>
            <li>
              <a href="https://wa.me/919131341638?text=Hi%20Kamlesh%20Sir%2C%20I%20want%20to%20know%20about%20your%20courses" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 mt-1 bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-medium rounded-lg px-3.5 py-2 transition">
                <MessageCircle className="w-3.5 h-3.5" /> Chat on WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">
        © {new Date().getFullYear()} CodeWithKamlesh · All rights reserved · Made with care in Indore
      </div>
    </footer>
  );
}
